import { useState } from 'react'
import {
  AddMailButton,
  Client,
  ClientAddress,
  ClientCompanyName,
  ClientName,
  ClientsListContent,
  ClientsListWrapper,
  DisplayMore,
  EmptyListWrapper,
  FirstLine,
  FirstLineLeft,
  FirstLineRight,
  FundsSource,
  LastLine,
  LastLineLeft,
  LastLineRight,
  MailOption,
  PendingMails,
  RegistrationDate,
  Rules
} from './styles'
import Template from 'pages/components/Template'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import SearchBar from 'components/SearchBar'
import { OkIcon, NotOkIcon } from 'components/Icons'
import ArrowDownIcon from 'globals/icons/arrow-down.svg'
import MailIcon from 'globals/icons/mail.svg'
import Button from 'components/Button'
import { useLocation } from 'react-router-dom'
import { QueryLazyOptions, useLazyQuery } from '@apollo/client'
import { format } from 'date-fns'
import {
  LIST_DOMICILIATIONS,
  ListDomiciliationsPrefilter,
  ListDomiciliationsResult,
  ListDomiciliationsVariables
} from 'pages/ClientsList/queries'
import Loader, { LoaderContainer } from 'components/Loader'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { SetState } from 'globals'
import ClientStatus from 'components/ClientStatus'
import { Domiciliation } from 'globals/models/Domiciliation'
import AddMail from 'pages/AddMail'
import { displayErrors } from 'globals/graphql'

const ClientsList = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const queryUrl = new URLSearchParams(location.search)
  const queryFilter = queryUrl.get('filter')

  const filter: ListDomiciliationsPrefilter =
    !queryFilter || !['all', 'activated', 'pending_partner_check', 'canceled'].includes(queryFilter) ?
      'activated' :
      queryFilter as ListDomiciliationsPrefilter


  const [domiciliations, setDomiciliations] = useState<Domiciliation[]>([])
  const [page, setPage] = useState(1)
  const [searchText, setSearchText] = useState<string | undefined>(queryUrl.get('search') || undefined)

  const [activeFilter, setActiveFilter] = useState<ListDomiciliationsPrefilter>(filter)
  const [showAddMail, setShowAddMail] = useState(false)
  const [addMailCenter, setAddMailCenter] = useState<Domiciliation | undefined>(undefined)
  const [noMoreDomiciliations, setNoMoreDomiciliations] = useState(false)

  const [listDomiciliations, listDomiciliationsQuery] =
    useLazyQuery<ListDomiciliationsResult, ListDomiciliationsVariables>(
        LIST_DOMICILIATIONS,
        {
          ...displayErrors,
          variables: {
            prefilter: activeFilter,
            page: page
          },
          onCompleted: onDomiciliationsRetrieved(domiciliations, setDomiciliations, setNoMoreDomiciliations),
          fetchPolicy: 'no-cache'
        }
    )
  const [sentryRef] = useInfiniteScroll({
    loading: listDomiciliationsQuery.loading,
    hasNextPage: !noMoreDomiciliations &&
     (
       (listDomiciliationsQuery.data && listDomiciliationsQuery.data.domiciliations.length > 0) ||
        domiciliations.length <= 0
     ),
    delayInMs: 1000,
    rootMargin: '0px 0px 400px 0px',
    onLoadMore:
      fetchMoreDomiciliations(
          searchText,
          activeFilter,
          page,
          listDomiciliations,
          setPage
      )
  })

  return (
    <Template
      noPadding
      noScroll
    >
      {(currentUser, currentCenters) =>
        <ClientsListWrapper>
          <ClientsListContent noScroll={showAddMail}>
            <Header
              title={t('clients.title')}
              currentUser={currentUser}
              currentCenters={currentCenters}
            />

            <SearchBar
              activeFilter={activeFilter}
              searchOnTheFly={true}
              marginBottom={true}
              onSearch={
                onSearch(
                    activeFilter,
                    listDomiciliations,
                    setDomiciliations,
                    setPage,
                    setSearchText,
                    setNoMoreDomiciliations
                )}
              onChangeFilter={
                onChangeFilter(
                    searchText,
                    listDomiciliations,
                    setDomiciliations,
                    setPage,
                    setActiveFilter,
                    setNoMoreDomiciliations
                )}
            />


            {(domiciliations.length === 0 && noMoreDomiciliations) ?
        <EmptyListWrapper>
            Aucun client dans cette catégorie à ce jour
        </EmptyListWrapper> :
          domiciliations.map((dom) =>
            <Client
              key={dom.id}
              translucent={showAddMail && !!addMailCenter && addMailCenter.id !== dom.id}
              status={dom.status}
            >
              <FirstLine>
                <FirstLineRight>
                  <ClientStatus
                    status={dom.status}
                  />

                  <RegistrationDate>
                    {t('clients.registration-date', { date: format(new Date(dom.clientSince), 'dd/MM/yyyy') })}
                  </RegistrationDate>
                  <FundsSource>
                    €€ {dom.firstPaymentCountryOrigin}
                  </FundsSource>
                </FirstLineRight>

                <FirstLineLeft>
                  <ClientCompanyName>
                    {dom.society.name} {dom.society.brandName &&`(${dom.society.brandName}) `}
                    <i>{dom.society.legalStatus}</i>
                  </ClientCompanyName>

                  <ClientName>
                    {dom.firstname} {dom.lastname}
                  </ClientName>
                </FirstLineLeft>
              </FirstLine>

              {
                !dom.address?.ownAddress && dom.forwardingAddress &&
                <ClientAddress>
                  {dom.forwardingAddress.streetAddress},&nbsp;
                  {dom.forwardingAddress.postalCode} {dom.forwardingAddress.city}
                </ClientAddress>
              }

              <LastLine>
                <LastLineLeft>
                  <Rules>
                    <MailOption>
                      {t('global.id')} : {dom.id}
                    </MailOption>

                    {(dom.subscriptionState == 'payment_failed') &&
                      <MailOption>
                        {t('mails.processing.unpaid')}
                        <NotOkIcon />
                      </MailOption>
                    }

                    <MailOption>
                      {t('clients.procuration')}
                      {dom.postalProcurationReady ?
                        <OkIcon /> :
                        <NotOkIcon />
                      }
                    </MailOption>

                    <MailOption>
                      {t('clients.mail-options.' + dom.userMailOption)}
                    </MailOption>

                    { dom.npai &&
                      <MailOption color={'red'}>
                        NPAI
                        <NotOkIcon />
                      </MailOption>
                    }
                  </Rules>

                  <DisplayMore
                    to={`/client/${dom.id}`}
                  >
                    <span>{t('clients.more')}</span>

                    <img src={ArrowDownIcon} alt="More" />
                  </DisplayMore>
                </LastLineLeft>

                <LastLineRight>
                  <PendingMails>
                    <img src={MailIcon} alt="Mail" />
                    {t('clients.pending-mails', { number: dom.pendingUserMailsCount })}
                  </PendingMails>

                  <AddMailButton>
                    <Button
                      disabled={dom.status != 'activated'}
                      onClick={addMail(dom, setShowAddMail, setAddMailCenter)}
                    >
                      {t('clients.add-mail')}
                    </Button>
                  </AddMailButton>

                  <DisplayMore
                    to={'/client/' + dom.id}
                    mobile
                  >
                    <span>{t('clients.more')}</span>
                    <img src={ArrowDownIcon} alt="More" />
                  </DisplayMore>
                </LastLineRight>
              </LastLine>
            </Client>
          )
            }

            {(!noMoreDomiciliations && !listDomiciliationsQuery.loading) &&
            <LoaderContainer ref={sentryRef}>
              <Loader />
            </LoaderContainer>
            }
          </ClientsListContent>

          <AddMail
            domiciliation={addMailCenter}
            visible={showAddMail && !!addMailCenter}
            onClose={hideAddMail(setShowAddMail)}
          />
        </ClientsListWrapper>
      }
    </Template>
  )
}

const onDomiciliationsRetrieved = (
    domiciliations: Domiciliation[],
    setDomiciliations: SetState<Domiciliation[]>,
    setNoMoreDomiciliations: SetState<boolean>
) => (listDomiciliations: ListDomiciliationsResult) => {
  displayErrors.onCompleted(listDomiciliations)

  setDomiciliations(
      domiciliations.concat(listDomiciliations.domiciliations)
  )

  if (listDomiciliations.domiciliations.length === 0) {
    setNoMoreDomiciliations(true)
  }
}

const onChangeFilter = (
    searchText: string | undefined,
    fetch: (options: QueryLazyOptions<ListDomiciliationsVariables>) => void,
    setDomiciliations: SetState<Domiciliation[]>,
    setPage: SetState<number>,
    setActiveFilter: SetState<ListDomiciliationsPrefilter>,
    setNoMoreDomiciliations: SetState<boolean>
) => (filter: ListDomiciliationsPrefilter) => {
  setActiveFilter(filter)
  setDomiciliations([])
  setNoMoreDomiciliations(false)

  fetch({
    variables: {
      searchTerm: searchText,
      prefilter: filter,
      page: 1
    }
  })
  setPage(2)
}

const onSearch = (
    activeFilter: ListDomiciliationsPrefilter,
    fetch: (options: QueryLazyOptions<ListDomiciliationsVariables>) => void,
    setDomiciliations: SetState<Domiciliation[]>,
    setPage: SetState<number>,
    setSearchText: SetState<string | undefined>,
    setNoMoreDomiciliations: SetState<boolean>
) => (searchText: string) => {
  setSearchText(searchText)
  setDomiciliations([])
  setNoMoreDomiciliations(false)

  fetch({
    variables: {
      searchTerm: searchText,
      prefilter: activeFilter,
      page: 1
    }
  })
  setPage(2)
}

const fetchMoreDomiciliations = (
    searchText: string | undefined,
    prefilter: ListDomiciliationsPrefilter,
    page: number,
    fetch: (options: QueryLazyOptions<ListDomiciliationsVariables>) => void,
    setPage: SetState<number>
) => () => {
  fetch({
    variables: {
      searchTerm: searchText,
      prefilter,
      page
    }
  })
  setPage(page + 1)
}

const addMail = (
    domiciliation: Domiciliation,
    setShowAddMail: SetState<boolean>,
    setAddMailCenter: SetState<Domiciliation | undefined>
) => () => {
  setAddMailCenter(domiciliation)
  setShowAddMail(true)
}

const hideAddMail = (setShowAddMail: SetState<boolean>) => () =>
  setShowAddMail(false)

export default ClientsList
